// RETURNING add/remove-item planner. RETURNING is a trailing comma-separated
// column list on INSERT / UPDATE / DELETE (`structure.returning`), so the
// applicator is statement-kind-agnostic. `add` appends onto the existing list
// (via the generic list helper) or synthesizes a fresh ` RETURNING <text>` at
// the statement tail — after any structured tails (VALUES / ON CONFLICT / ON
// DUPLICATE) and before a trailing `;`. `remove` of the only item drops the
// RETURNING keyword along with it, mirroring `planRemoveHaving`.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Structural Edit Applicator — add/remove.

import type { PlannedByteEdit, StructuralEdit } from '../types';

import type { EditCtx } from './applyStructuralEdit';
import { planListInsert, planListRemove } from './applyStructuralEdit.lists';
import {
  deleteEdit,
  findKeyword,
  insertEdit,
  scanStatementKeywords,
  trimTrailingWs,
  type ListItem,
} from './applyStructuralEdit.shared';

export function planReturningEdit(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'add-returning-item' | 'remove-returning-item' }>,
): readonly PlannedByteEdit[] {
  if (edit.op === 'add-returning-item') return planAddReturning(ctx, edit);
  return planRemoveReturning(ctx, edit);
}

function planAddReturning(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'add-returning-item' }>,
): readonly PlannedByteEdit[] {
  const items: readonly ListItem[] = ctx.structure.returning ?? [];
  if (items.length > 0) {
    return [planListInsert(ctx, items, edit.text, undefined, 'add-returning-item')];
  }
  // No RETURNING clause — synthesize ` RETURNING <text>` at the statement tail.
  return [insertEdit(ctx, returningInsertOffset(ctx), ` RETURNING ${edit.text}`, 'add-returning-item:empty')];
}

function planRemoveReturning(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'remove-returning-item' }>,
): readonly PlannedByteEdit[] {
  const items = ctx.structure.returning ?? [];
  if (edit.index < 0 || edit.index >= items.length) return [];
  if (items.length === 1) {
    // Remove the only item AND the leading RETURNING keyword.
    const returningKw = findKeyword(scanStatementKeywords(ctx), 'RETURNING');
    if (returningKw) {
      return [deleteEdit(
        ctx, returningKw.start, items[0].range.endOffset, 'remove-returning-item:with-keyword',
      )];
    }
    return [deleteEdit(
      ctx, items[0].range.startOffset, items[0].range.endOffset, 'remove-returning-item:item-only',
    )];
  }
  const planned = planListRemove(ctx, items, edit.index, 'remove-returning-item');
  return planned ? [planned] : [];
}

// The insertion offset for a synthesized RETURNING clause: the end of the
// statement content, stepping back over trailing whitespace and a single
// trailing `;` (which may or may not sit inside the statement range).
function returningInsertOffset(ctx: EditCtx): number {
  const { source, statementInfo } = ctx;
  const lo = statementInfo.range.startOffset;
  let e = trimTrailingWs(source, lo, statementInfo.range.endOffset);
  if (e > lo && source[e - 1] === ';') {
    e = trimTrailingWs(source, lo, e - 1);
  }
  return e;
}
